import axios from 'axios';
import type { AxiosInstance } from 'axios';
import { createAuthApi } from './requests/auth';
import { createUserApi } from './requests/user';
import { createPlantApi } from './requests/plant';
import { createInverterApi } from './requests/inverter';
import { createRtuApi } from './requests/rtu';
import { createStatisticsApi } from './requests/statistics';
import { createEventApi } from './requests/event';
import { createCodeApi } from './requests/code';
import { createConsentApi } from './requests/consent';
import { createWeatherApi } from './requests/weather';
import { createKpxApi } from './requests/kpx';
import { createReportApi } from './requests/report';
import { createRevenueApi } from './requests/revenue';
import { createHttp, type EndpointRequestConfig } from './http';

export interface CreateHevitonApiAppLegacyOptions {
  baseURL: string;
  timeoutMs?: number;
  getAccessToken: () => string | null;
  /**
   * access 재발급 함수 — **composition 이 소유하는 공통 single-flight**. 401(재시도 가능 endpoint)에서
   * 인터셉터가 호출해 새 access token 을 받고 재시도한다. **null = 재발급 불가(원 요청 실패)** 일 뿐이고,
   * 세션을 끝낼지(로그아웃)는 실패 사유를 아는 composition 이 이 함수 안에서 스스로 판단한다.
   * 세 API(app-legacy·v2·web-legacy)가 같은 함수를 주입받아 하나의 refresh 로 coalesce 된다.
   *
   * refresh HTTP 자체(엔드포인트·refresh 토큰 bearer 헤더)도 composition 이 `auth.refreshToken(...)` 을
   * 직접 호출해 수행한다 — 본 패키지의 request interceptor 는 refresh 를 특별 취급하지 않는다.
   */
  refreshToken: () => Promise<string | null>;
  /**
   * 세션 확정 만료 통보 — **재발급된 access 마저 401 로 거부된 경우에만** 호출된다.
   * 재발급 실패 자체는 composition 이 사유를 보고 처리하므로 여기로 오지 않는다.
   */
  onAuthError: () => void;
  onNetworkError?: () => void;
  onServerError?: (status: number) => void;
  onMaintenance?: () => void;
}

function buildClient(opts: CreateHevitonApiAppLegacyOptions): AxiosInstance {
  const client = axios.create({
    baseURL: opts.baseURL,
    timeout: opts.timeoutMs ?? 10_000,
    headers: { 'Content-Type': 'application/json' },
  });

  // Request interceptor: Authorization 헤더 담당 — 판단 근거는 **엔드포인트가 선언한 `requiresAuth`** 하나뿐.
  //   true  → store access 를 **무조건 덮어쓴다**. "상위(composition)가 항상 최신 토큰을 들고 있다"가 전제 —
  //           401 재시도도 이 경로로 새 토큰을 받으므로 재시도 측이 헤더를 따로 손댈 필요가 없다.
  //   false → 헤더에 **손대지 않는다**(지우지도 않음). 무인증(login)이거나 호출처가 헤더를 소유하는 경우:
  //           refresh=refresh 토큰 bearer / logout=teardown 후 access 스냅샷.
  client.interceptors.request.use((config) => {
    const { endpoint } = config as EndpointRequestConfig;
    if (!endpoint?.requiresAuth) return config;
    const access = opts.getAccessToken();
    if (access) config.headers.set('Authorization', `Bearer ${access}`);
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      if (axios.isCancel(error)) return Promise.reject(error);

      if (!error.response) {
        opts.onNetworkError?.();
        return Promise.reject(error);
      }

      const { status } = error.response;
      const config = error.config as EndpointRequestConfig | undefined;

      // 401 + `retryAfterTokenRefresh` endpoint → 공통 reissue 후 1회 재시도.
      // false(login·refresh·logout·비번/코드 검증)는 401 시맨틱이 "세션 만료"가 아니라 "입력 틀림/재발급
      // 불가"라 여기 안 걸린다 — _retried 도 안 세워 아래 재-401 가드도 skip → 강제 로그아웃 안 함(호출처가 처리).
      //
      // ⚠️ 재발급 실패(null)에 **`onAuthError` 를 부르지 않는다** — 세션 종료 판정은 `refreshToken` 소유자
      // (composition)의 몫이다. 이유 둘: (1) 여기선 실패 **사유**를 모른다(401=세션만료 vs 5xx·타임아웃=판정불가).
      // (2) `refreshToken` 은 single-flight 라 1회 도는데 대기 중이던 요청 N개가 각자 부르면 세션 종료가 N번
      // 통보된다. composition 이 사유를 보고 1회만 종료하고, 여기선 원 요청을 실패시키기만 한다.
      if (status === 401 && config?.endpoint?.retryAfterTokenRefresh && !config._retried) {
        config._retried = true;
        const newAccess = await opts.refreshToken();
        if (!newAccess) return Promise.reject(error);
        // retry — request interceptor 가 새 access token 자동 부여
        return client(config);
      }

      // 재시도까지 했는데 또 401 = **방금 재발급받은 access 마저 거부** → 세션 확정 만료 → 강제 로그아웃.
      // 이건 재발급이 성공으로 끝나 composition 이 볼 수 없는 사실이라, `onAuthError` 가 남는 유일한 자리다.
      // (refresh endpoint 자체의 401 은 retryAfterTokenRefresh=false 라 _retried 미설정 → 여기 안 걸린다.)
      if (status === 401 && config?._retried) {
        opts.onAuthError();
        return Promise.reject(error);
      }

      if (status === 503) {
        opts.onMaintenance?.();
        return Promise.reject(error);
      }

      if (status >= 500) {
        opts.onServerError?.(status);
        return Promise.reject(error);
      }

      return Promise.reject(error);
    },
  );

  return client;
}

export function createHevitonApiAppLegacy(opts: CreateHevitonApiAppLegacyOptions) {
  // request 함수들은 raw client 가 아니라 `http`(Endpoint 래퍼)를 받는다 — 경로 해석 + 정책 전달 일원화.
  const http = createHttp(buildClient(opts));
  return {
    auth: createAuthApi(http),
    user: createUserApi(http),
    plant: createPlantApi(http),
    inverter: createInverterApi(http),
    rtu: createRtuApi(http),
    statistics: createStatisticsApi(http),
    event: createEventApi(http),
    code: createCodeApi(http),
    consent: createConsentApi(http),
    weather: createWeatherApi(http),
    kpx: createKpxApi(http),
    report: createReportApi(http),
    revenue: createRevenueApi(http),
  };
}

export type HevitonApiAppLegacy = ReturnType<typeof createHevitonApiAppLegacy>;
